import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonModule, ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from "@progress/kendo-angular-indicators";
import { LayoutModule } from "@progress/kendo-angular-layout";
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { PopupModule } from '@progress/kendo-angular-popup';
import { timezoneNames } from '@progress/kendo-date-math';
import { menuIcon, searchIcon, SVGIcon } from "@progress/kendo-svg-icons";
import { LogoutOnlyPopupComponent } from "@src/app/shared/components/popup/templates/logout-only-popup.component";
import { FeatureFlagDirective } from '@src/app/shared/directives/feature-flag.directive';
import { UserHasAnyScopeDirective } from '@src/app/shared/directives/user-has-any-scope.directive';
import { PermissionScope } from '@src/app/shared/enums';
import { User } from '@src/app/shared/models';
import { CoreUtilsService } from '@src/app/shared/services/core-utils/core-utils.service';
import { ResponsiveService } from '@src/app/shared/services/responsive.service';
import * as fromStore from '@src/app/store/conventional';
import { PopupCompanyTemplateComponent } from '../../../shared/components/popup/templates/company-template.component';
import { PopupUserSettingsComponent } from '../../../shared/components/popup/templates/popup-user-settings/popup-user-settings.component';
import { UniversalSearchComponent } from "../../../universal-search/components/universal-search.component";
import { EnvironmentRibbonComponent } from './components/environment-ribbon/environment-ribbon.component';

@Component({
    selector: 'cs-navbar',
    imports: [
        CommonModule,
        NavigationModule,
        DropDownsModule,
        PopupModule,
        ButtonModule,
        IconsModule,
        RouterModule,
        LayoutModule,
        ButtonsModule,
        IndicatorsModule,
        PopupCompanyTemplateComponent,
        PopupUserSettingsComponent,
        LogoutOnlyPopupComponent,
        DialogModule,
        EnvironmentRibbonComponent,
        UniversalSearchComponent,
        FeatureFlagDirective,
        UserHasAnyScopeDirective
    ],
    styleUrl: './navbar.component.scss',
    template: `
    <kendo-appbar position="top"
                  class="relative flex h-[48px] w-full min-w-[340px] flex-nowrap items-center justify-between bg-theme-primary px-[24px] pb-1 pt-[4.5px] text-white shadow-none">
      <kendo-appbar-section class="flex gap-5">
        <a [routerLink]="mainUrl">
          <img class="h-[19px] pl-[6px]" src="assets/logo.svg" alt="Cargo Signal Logo">
        </a>
        @if (userLoaded()) {
          <cs-popup-company [user]="user()"></cs-popup-company>
        } @else {
          <kendo-skeleton
            role="status"
            aria-label="loading user companies"
            class="rounded bg-theme-primary-foreground"
            shape="rectangle"
            animation="pulse"
            width="206px"
            height="30px"
          ></kendo-skeleton>
        }
      </kendo-appbar-section>

      @if (userLoaded()) {
      <kendo-appbar-section *csFeatureFlag="'universalSearch'" class="hidden min-w-0 flex-[0_1_465px]">
        <cs-universal-search class="grow"></cs-universal-search>
      </kendo-appbar-section>
      }

      <kendo-appbar-section class="flex justify-end gap-x-6">
        <button class="lg:hidden" kendoButton [svgIcon]="searchIcon" fillMode="flat"
          (click)="openSearch()"></button>
        <button class="lg:hidden" kendoButton [svgIcon]="menuIcon" fillMode="flat"
                (click)="openDrawer()"></button>
        <nav class="hidden gap-x-4 lg:flex">
          <a *csUserHasAnyScope="[PermissionScope.READ_SHIPMENTS]" [routerLink]="['/shipments']" routerLinkActive="active"
             class="max-h-[40px] border-b-2 border-transparent px-2 py-[9px] text-[14px] hover:border-[#0DCAF0]">Shipments</a>
          <a *csUserHasAnyScope="[PermissionScope.READ_FACILITIES]" [routerLink]="['/facilities']" routerLinkActive="active"
             class="max-h-[40px] border-b-2 border-transparent px-2 py-[9px] text-[14px] hover:border-[#0DCAF0]">Facilities</a>
          <a *csUserHasAnyScope="[PermissionScope.READ_DEVICES, PermissionScope.READ_LABELS, PermissionScope.READ_SOURCE_GROUPS]" [routerLink]="['/data-sources']" routerLinkActive="active"
             class="max-h-[40px] border-b-2 border-transparent px-2 py-[9px] text-[14px] hover:border-[#0DCAF0]">Data
            Sources</a>
          @if(utils.isAdminTabEnabled()) {
            <a *csUserHasAnyScope="[PermissionScope.READ_ADMIN_PANEL]" [routerLink]="['/admin']" routerLinkActive="active" class="max-h-[40px] border-b-2 border-transparent px-2 py-[9px] text-[14px] hover:border-[#0DCAF0]">Admin</a>
          }
         
          @if (userLoaded()) {
            <div class="user-settings-popup flex items-center pl-2">
              <cs-popup-user-settings [user]="user()"></cs-popup-user-settings>
            </div>
          } @else if (userLoading()) {
            <kendo-skeleton
              class="bg-theme-primary-foreground"
              shape="circle"
              animation="pulse"
              width="32px"
              height="32px"
            ></kendo-skeleton>
          } @else {
            <div class="user-settings-popup flex items-center pl-2">
              <cs-logout-only-popup aria-label="load user failed"></cs-logout-only-popup>
            </div>
          }
        </nav>
      </kendo-appbar-section>

    </kendo-appbar>
    <!--container for all modal dialogs-->
    <div kendoDialogContainer id="dialog-container"></div>
    @if (env !== 'prod') {
      <cs-environment-ribbon></cs-environment-ribbon>
    }
  `
})
export class NavbarComponent {
  private store = inject(Store<fromStore.State>)
  public utils = inject(CoreUtilsService)
  private responsiveService = inject(ResponsiveService);
  public mainUrl: string = '/';

  public user = input<User>();
  PermissionScope = PermissionScope;
  public menuIcon: SVGIcon = menuIcon;
  public searchIcon: SVGIcon = searchIcon;
  public allTimezones: string[] = timezoneNames();
  public userLoaded = toSignal(this.store.select(
    fromStore.selectUserLoaded),
    { initialValue: false }
  );
  public userLoading = toSignal(this.store.select(
    fromStore.selectUserLoading,
    { initialValue: true }
  ))
  public env = this.utils.getEnvironmentVariable("environment") as string;
  public openDrawer(): void {
    this.responsiveService.toggleState();
  }
  public openSearch(): void {
    this.responsiveService.toggleSearch();
  }

}
